import { decode } from 'he';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { ButtonInLink } from '@hivebrite/design-system/ButtonInLink';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { Link, FakeLink } from '@hivebrite/design-system/Link';
import { linkEffectForChildren } from '@hivebrite/design-system/Theme';
import { UserPresentation } from '@hivebrite/design-system/UserPresentation';

import AddFolder from 'hb-react/shared/icons/AddFolder';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import {
  canDownloadFile,
  isFileAnArticle,
} from 'hb-react/shared/utils/files/fileContentTypes';
import { isMobileApp } from 'hb-react/shared/utils/mobile';
import * as mobileDeviceFromUserAgent from 'helpers/mobileDeviceFromUserAgent';

import { incrementDownload } from '../utils/fileAnalytics';

export const MessagingButton = ({ userId }) => (
  <Link
    to={Routes.messaging_new_conversation_path({
      recipient_ids: userId,
    })}
    tabIndex={-1}
  >
    <Button>{__('Send message')}</Button>
  </Link>
);

MessagingButton.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

const HeaderComponent = ({ ownerId, name, ownerLink }) => (
  <HeaderUserLink
    to={ownerLink || Routes.user_portfolio_path(ownerId)}
    styledOnlyOnHover
  >
    {name}
  </HeaderUserLink>
);

const Infos = ({
  file,
  user,
  hasMessagingButton = false,
  folderUrl,
  postContent,
}) => {
  if (!file && user) {
    const { id, photo_thumb_url: photoThumbUrl, name } = user;

    return (
      <>
        <UserPresentation
          avatar={photoThumbUrl}
          name={<HeaderComponent ownerId={id} name={name} />}
        />
        {hasMessagingButton && <MessagingButton userId={id} />}
      </>
    );
  }

  if (!file || !Object.keys(file).length) {
    return null;
  }

  const {
    owner,
    created_at: createdAt,
    download_url: downloadUrl,
    name,
    description,
    file_type: fileType,
    uuid,
    is_downloadable: downloadable,
  } = file;

  const hasDownloadButton = canDownloadFile({
    fileType,
  });

  const canBeDownloaded = hasDownloadButton && downloadable;

  const isArticle = isFileAnArticle(fileType);

  // Need to open the web app in the browser to download content on iOS mobile app
  const targetForDownloadButton =
    isMobileApp() && mobileDeviceFromUserAgent.iOS() ? '_blank' : '_self';

  return (
    <>
      {owner && (
        <Box display="block" mb={5} mr={{ _: 10, m: 6 }}>
          <UserPresentation
            avatar={owner.photo || owner.photo_thumb_url || owner.pictureUrl}
            name={
              <HeaderComponent
                ownerId={owner.id}
                name={owner.name}
                ownerLink={owner.link}
              />
            }
            headline={dayjs(createdAt).fromNow()}
          />
        </Box>
      )}
      <Box mb={5} display="block">
        {!postContent && (
          <Box justifyContent="space-between" alignItems="center">
            <StyledDotdotdot clamp={2}>
              <Box
                as="span"
                font={postContent ? 'p' : 'h3-semibold'}
                display="inline"
              >
                {decode(name)}
              </Box>
            </StyledDotdotdot>

            {hasDownloadButton && owner && (
              <Button
                onClick={() =>
                  window.open(downloadUrl, targetForDownloadButton)
                }
                variant="secondary"
                ml={3}
              >
                {s__('Portfolio|Download file')}
              </Button>
            )}
            {isArticle && (
              <ButtonInLink
                linkProps={{
                  to: Routes.show_article_path(uuid),
                }}
                variant="secondary"
                ml={3}
              >
                {s__('Portfolio|Open article')}
              </ButtonInLink>
            )}
          </Box>
        )}

        {description && (
          <Box font="body-regular-asphalt" mt={2} display="block">
            <pre>{description}</pre>
          </Box>
        )}
        {postContent && (
          <WrapperContent
            font="body-regular-asphalt"
            mt={2}
            display="block"
            dangerouslySetInnerHTML={{ __html: postContent }}
          />
        )}
        {folderUrl && (
          <FolderLink to={folderUrl} mt={2}>
            <AddFolder width="30px" />
            <FakeLink ml={1}>{__('Go to folder')}</FakeLink>
          </FolderLink>
        )}
      </Box>
      {canBeDownloaded && !owner && (
        <Box
          justifyContent="flex-end"
          onClick={() => {
            if (file.uuid) {
              incrementDownload(file.uuid);
            }
          }}
        >
          <Button
            onClick={() => window.open(downloadUrl, targetForDownloadButton)}
            variant="secondary"
          >
            {s__('Portfolio|Download file')}
          </Button>
        </Box>
      )}
      {hasMessagingButton && user && <MessagingButton userId={user.id} />}
    </>
  );
};

Infos.propTypes = {
  file: PropTypes.shape({
    owner: PropTypes.shape({
      name: PropTypes.string,
      photo: PropTypes.string,
    }),
    created_at: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    content_type: PropTypes.string,
    file_type: PropTypes.string,
    download_url: PropTypes.string,
    extension: PropTypes.string,
  }),
  folderUrl: PropTypes.string,
  hasMessagingButton: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    photo_thumb_url: PropTypes.string,
    name: PropTypes.string,
  }),
};

export default Infos;

const WrapperContent = styled(Box)`
  ${linkEffectForChildren}
`;

const StyledDotdotdot = styled(Dotdotdot)`
  overflow-wrap: break-word;
  flex: 1;
  max-width: 92%;
`;

const FolderLink = styled(Link)`
  display: flex;
  align-items: center;
`;

const HeaderUserLink = styled(Link)`
  &:hover {
    text-underline-position: auto;
  }
`;
